import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'

export type AnalysisSectionType =
  | 'purchase_listings'
  | 'rental_listings'
  | 'transactions'
  | 'building_permits'
  | 'amenities'
  | 'transport'
  | 'demographics'
  | 'employment'
  | 'history'
  | 'energy'
  | 'restrictions'
  | 'zoning_rules'
  | 'zoning_data'
  | 'sunlight'
  | 'sunlight_study'
  | 'taxes'
  | 'electricity'
  | 'noise'
  | 'purchase_market'
  | 'rental_market'
  | null

interface AnalysisVectorTileConfig {
  tileUrl: string
  sourceLayer: string
  pointColor?: string
}

interface AnalysisMapSectionData {
  listings?: any[]
  transactions?: any[]
  building_permits?: any[]
  amenities?: any
  transport?: any
  restrictions?: any[]
  zoningData?: any
  sunlightConfig?: {
    date: Date
  }
  vectorTileConfig?: AnalysisVectorTileConfig
  [key: string]: any
}

interface AnalysisMapSectionDataContextState {
  activeSection: AnalysisSectionType
  sectionData: AnalysisMapSectionData
  hoveredItemId: string | number | null
  selectedItemId: string | number | null
  flyToCoordinates: [number, number] | null
  onItemClick: ((item: any) => void) | null
  setActiveSection: (section: AnalysisSectionType) => void
  updateSectionData: (data: Partial<AnalysisMapSectionData>) => void
  clearSectionData: () => void
  setHoveredItemId: (id: string | number | null) => void
  setSelectedItemId: (id: string | number | null) => void
  setFlyToCoordinates: (coordinates: [number, number] | null) => void
  setOnItemClick: (handler: ((item: any) => void) | null) => void
}

const AnalysisMapSectionDataContext = createContext<
  AnalysisMapSectionDataContextState | undefined
>(undefined)

interface AnalysisMapSectionDataProviderProps {
  children: ReactNode
  /**
   * Delegates fly-to and selection to a host that owns its own map state.
   *
   * Land and building analysis mount this provider inside their own
   * MapSectionDataProvider, and the map reads that outer one. Without
   * delegation the two hold separate state: a shared tab would set a fly-to
   * here and the map, listening to the outer context, would never move —
   * which is exactly what happened on the amenities tab.
   */
  onFlyToCoordinates?: (coordinates: [number, number] | null) => void
  onSelectedItemIdChange?: (id: string | number | null) => void
}

export const AnalysisMapSectionDataProvider: React.FC<
  AnalysisMapSectionDataProviderProps
> = ({ children, onFlyToCoordinates, onSelectedItemIdChange }) => {
  const [activeSection, setActiveSection] = useState<AnalysisSectionType>(null)
  const [sectionData, setSectionData] = useState<AnalysisMapSectionData>({})
  const [hoveredItemId, setHoveredItemId] = useState<string | number | null>(
    null
  )
  const [selectedItemId, setSelectedItemId] = useState<string | number | null>(
    null
  )
  const [flyToCoordinates, setFlyToCoordinates] = useState<
    [number, number] | null
  >(null)
  const [onItemClick, setOnItemClick] = useState<((item: any) => void) | null>(
    null
  )

  /** Keep local state in step too, so consumers of this context stay correct. */
  const handleFlyToCoordinates = useCallback(
    (coordinates: [number, number] | null) => {
      setFlyToCoordinates(coordinates)
      onFlyToCoordinates?.(coordinates)
    },
    [onFlyToCoordinates]
  )

  const handleSelectedItemIdChange = useCallback(
    (id: string | number | null) => {
      setSelectedItemId(id)
      onSelectedItemIdChange?.(id)
    },
    [onSelectedItemIdChange]
  )

  const updateSectionData = useCallback(
    (data: Partial<AnalysisMapSectionData>) => {
      setSectionData((prev) => ({ ...prev, ...data }))
    },
    []
  )

  const clearSectionData = useCallback(() => {
    setSectionData({})
  }, [])

  const contextValue: AnalysisMapSectionDataContextState = useMemo(
    () => ({
      activeSection,
      sectionData,
      hoveredItemId,
      selectedItemId,
      flyToCoordinates,
      onItemClick,
      setActiveSection,
      updateSectionData,
      clearSectionData,
      setHoveredItemId,
      setSelectedItemId: handleSelectedItemIdChange,
      setFlyToCoordinates: handleFlyToCoordinates,
      setOnItemClick,
    }),
    [
      activeSection,
      sectionData,
      hoveredItemId,
      selectedItemId,
      flyToCoordinates,
      onItemClick,
      updateSectionData,
      clearSectionData,
      handleFlyToCoordinates,
      handleSelectedItemIdChange,
    ]
  )

  return (
    <AnalysisMapSectionDataContext.Provider value={contextValue}>
      {children}
    </AnalysisMapSectionDataContext.Provider>
  )
}

export const useAnalysisMapSectionData =
  (): AnalysisMapSectionDataContextState => {
    const context = useContext(AnalysisMapSectionDataContext)
    if (context === undefined) {
      throw new Error(
        'useAnalysisMapSectionData must be used within an AnalysisMapSectionDataProvider'
      )
    }
    return context
  }

/** Optional version that returns undefined when used outside provider */
export const useAnalysisMapSectionDataOptional = ():
  | AnalysisMapSectionDataContextState
  | undefined => {
  return useContext(AnalysisMapSectionDataContext)
}

export type {
  AnalysisMapSectionData,
  AnalysisMapSectionDataContextState,
  AnalysisVectorTileConfig,
}
